/**
 * VoiceAndChatLaunch — public 1-pager at /whats-new/voice-and-chat.
 * Doctor-friendly copy. Zero technical jargon (no WebRTC / STUN / peer-to-peer).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, Lock, Clock, UserPlus, Shield, Printer, ArrowRight, Mic, CheckCircle2, Heart } from 'lucide-react';

const LAUNCH = {
  slug: 'voice-and-chat',
  title: 'Patients can now call you directly — and your number stays private',
  published_at: '2026-02-10',
  summary: 'A patient on your DocSite can tap once and speak to you live — no missed calls, no number shared, no WhatsApp pings on your personal phone. You stay in control: open all day, fixed hours, or hand the line over to your receptionist.',
  keywords: [
    'private doctor calls',
    'doctor patient direct call privacy',
    'doctor receptionist forwarding',
    'DocSite voice calls',
    'private chat for doctors',
  ],
};

export default function VoiceAndChatLaunch() {
  const url = `${window.location.origin}/whats-new/${LAUNCH.slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org', '@type': 'NewsArticle',
    headline: LAUNCH.title, description: LAUNCH.summary, datePublished: LAUNCH.published_at,
    keywords: LAUNCH.keywords.join(', '), mainEntityOfPage: url,
    publisher: { '@type': 'Organization', name: 'DocSite', logo: { '@type': 'ImageObject', url: `${window.location.origin}/logo512.png` } },
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'What\'s New', item: `${window.location.origin}/whats-new` },
      { '@type': 'ListItem', position: 2, name: LAUNCH.title, item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{`${LAUNCH.title} · DocSite`}</title>
        <meta name="description" content={LAUNCH.summary} />
        <meta name="keywords" content={LAUNCH.keywords.join(', ')} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={LAUNCH.title} />
        <meta property="og:description" content={LAUNCH.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="article:published_time" content={LAUNCH.published_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 no-print">
          <Link to="/whats-new" className="text-sm text-sky-600 hover:underline">← All updates</Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">
            <Printer size={12} />Print / Save as PDF
          </button>
        </div>

        {/* Hero */}
        <div className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600 mb-3">Just Launched · 10 Feb 2026</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Patients can call you directly — <span className="text-emerald-600">your number stays private.</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">{LAUNCH.summary}</p>
        </div>

        {/* Why it matters */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          <Card icon={<Shield size={18} className="text-emerald-600" />} title="Your number stays private">
            Your phone number is never shown on your DocSite, never dialled, never shared. The patient calls you through DocSite — you hear them, they hear you, but neither side sees the other's mobile.
          </Card>
          <Card icon={<Clock size={18} className="text-emerald-600" />} title="Available when you choose">
            Set your call hours and days. Outside that window, callers see a polite "Doctor is currently offline" — no late-night rings, no missed-call guilt, no patient feeling ignored.
          </Card>
          <Card icon={<UserPlus size={18} className="text-emerald-600" />} title="Hand the line to your receptionist">
            Add your receptionist's email and patient calls ring on their phone instead. You can also choose to ring on both phones together — whoever picks up first answers.
          </Card>
        </div>

        {/* How it works */}
        <Section title="How a call works" icon={<Phone size={18} />}>
          <ol className="space-y-3 text-slate-700">
            <Step n={1}>A <b>patient</b> visits your DocSite, taps <b>"Call now"</b>, types their name and mobile.</Step>
            <Step n={2}>Your <b>phone buzzes</b> with the patient's name and reason. Your receptionist's phone buzzes too if you've added one.</Step>
            <Step n={3}>You tap <b>Accept</b>. Your phone asks once for microphone permission — that's the only setup you'll ever need.</Step>
            <Step n={4}>You and the patient are <b>now speaking</b> live. Crystal-clear voice, no third-party operator listening, no recording stored.</Step>
            <Step n={5}>Either of you taps <b>End call</b> when done. The duration is logged in your dashboard for your records.</Step>
          </ol>
        </Section>

        <div className="my-10 grid sm:grid-cols-2 gap-4">
          <FactRow icon={<Mic size={14} className="text-emerald-600" />} title="Voice only — for now"
                   body="No video this release. Voice is faster, uses less mobile data, and most patients prefer it. Video for follow-up consults is coming next quarter." />
          <FactRow icon={<CheckCircle2 size={14} className="text-emerald-600" />} title="Works on regular mobile data"
                   body="Any 4G or 5G network does the job. Strict office WiFi can sometimes block voice traffic — if that happens, the patient just switches to mobile data and tries again." />
          <FactRow icon={<Lock size={14} className="text-emerald-600" />} title="Part of the Turbo plan"
                   body="Voice calls are included in Turbo (₹999/month for the first 1,000 doctors). On Free or Coffee Premium, your patients still see chat and WhatsApp options as before." />
          <FactRow icon={<Heart size={14} className="text-emerald-600" />} title="Built for trust, not transactions"
                   body="No per-minute charge to you, no platform fee on the call, no advertisement injected. The call is just between you and your patient — the way medicine should be." />
        </div>

        {/* Chat tiers */}
        <Section title="Private chat now scales with your plan" icon={<MessageCircle size={18} />}>
          <p className="text-slate-600 mb-5">After every booking, the patient and you get a small private chat — for "running 10 minutes late" or "please bring past reports". Hard limits keep it focused, never spammy.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <TierCard plan="Free (Gold)" price="₹0" cap="3 messages each side" tone="slate" />
            <TierCard plan="Coffee Premium" price="₹299/mo" cap="5 messages each side" tone="amber" />
            <TierCard plan="Turbo" price="₹999/mo" cap="Unlimited" tone="emerald" badge />
          </div>
        </Section>

        {/* CTA */}
        <div className="mt-14 bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200 rounded-3xl p-8 sm:p-10 text-center no-print">
          <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Switch on voice calls in 60 seconds
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-6 text-sm">
            Open your dashboard → <b>Calls & Chat</b> → flip "Allow voice calls" → set your hours (or leave it always-on) → optionally add your receptionist's email. That's it.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/dashboard?tab=calls_chat"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl text-base font-semibold hover:bg-emerald-700"
              data-testid="launch-vc-go-dashboard">
              Open my dashboard <ArrowRight size={16} />
            </Link>
            <Link to="/upgrade"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-900 rounded-xl text-base font-semibold hover:border-slate-400">
              Upgrade to Turbo
            </Link>
          </div>
        </div>

        <footer className="mt-14 text-center text-xs text-slate-400">
          Published 10 Feb 2026 · DocSite product team · <Link to="/whats-new" className="text-sky-600 hover:underline">More updates →</Link>
        </footer>
      </div>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center gap-2 mb-2">{icon}<p className="font-semibold text-slate-900">{title}</p></div>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-5 flex items-center gap-2.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {icon}{title}
      </h2>
      {children}
    </section>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{n}</span>
      <span className="text-sm leading-relaxed">{children}</span>
    </li>
  );
}

function FactRow({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 mb-1">{icon}<p className="font-semibold text-slate-900 text-sm">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function TierCard({ plan, price, cap, tone, badge }) {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  };
  return (
    <div className={`relative rounded-2xl border-2 p-5 ${tones[tone]}`}>
      {badge && <span className="absolute -top-3 left-5 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide">Best value</span>}
      <p className="text-xs uppercase font-bold tracking-widest opacity-70">{plan}</p>
      <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{price}</p>
      <p className="text-sm mt-2 font-medium">{cap}</p>
    </div>
  );
}
